import classNames from "classnames";
import React, { type JSX } from "react";

import { IRadioChoiceField } from "../models/formFields";
import { FormInputFieldWrapper } from "./FormInputFieldWrapper";
import { useFormField } from "./hooks/useFormField";

const buildOptionHelpText = (
    str: string | JSX.Element | undefined,
): React.ReactNode => {
    if (!str) {
        return null;
    }

    return <div className="form__radio-option__help-text">{str}</div>;
};

export const FormRadioSelect = <T extends string>(
    props: IRadioChoiceField<T>,
): React.ReactNode => {
    const { type = "radio-choice", choices, legend } = props;

    const field = useFormField<T, HTMLInputElement>({
        ...props,
        type: type,
    });

    const inputProps = field.inputProps;

    const options = (choices || []).map((option) => {
        const isChecked = inputProps.value === option.value;
        const helpText = buildOptionHelpText(option.helpText);
        const optionClasses = classNames({
            "form__radio-option": true,
            "form__radio-option--checked": isChecked,
        });
        const optionLabelClasses = classNames({
            "form__radio-option__label": true,
            "form__radio-option__label--checked": isChecked,
        });
        return (
            <div className={optionClasses} key={option.value}>
                <label
                    className={optionLabelClasses}
                    htmlFor={`${props.id}-${option.value}`}
                >
                    <input
                        type="radio"
                        name={props.name}
                        id={`${props.id}-${option.value}`}
                        value={option.value}
                        checked={isChecked}
                        disabled={option.disabled}
                        aria-describedby={`${inputProps.name}_errors`}
                        onChange={inputProps.onChange}
                    />
                    <span>
                        {option.label}
                        {helpText}
                        {option.extraText}
                    </span>
                </label>
            </div>
        );
    });

    const fieldName = inputProps.name.split("-")[0];
    const optionsLegendClasses = classNames({
        "ada-screenreader-only":
            fieldName === "rating" || fieldName === "bottomline",
    });

    return (
        <FormInputFieldWrapper
            {...props}
            type={type}
            focused={field.focused}
            isRequired={field.isRequired}
            onLabelClick={field.onLabelClick}
        >
            <fieldset id={inputProps.id} className="form__radio-options">
                <legend className={optionsLegendClasses}>{legend}</legend>
                {options}
            </fieldset>
        </FormInputFieldWrapper>
    );
};
